/* Shipping eligibility only. The standard and clearance optimizers remain independent. */
(() => {
  if (window.FibroluxCutShipping) return;
  const limits = { length: 1000, width: 760, height: 760, weightGrams: 30000 };
  // ShipX Germany, checked 2026-09-09: DPD 9.90 EUR / UPS 14.90 EUR.
  // These additional tiers require a standard-profile cut order; clearance-only rules stay unchanged.
  const profileLimits = {
    dpd: {length:1200, width:500, height:500, weightGrams:31000, volume:157500000},
    ups: {length:2000, width:500, height:500, weightGrams:31000, volume:150000000}
  };
  const pairs = { '54057754263898': 54667718000986, '53991162085722': 54753612136794 };
  const dpdPairs = { '53991162085722': 54753752416602 };
  const props = item => item?.properties || {};
  const groupId = item => props(item)._restposten_plan_id ? `rest:${props(item)._restposten_plan_id}` : props(item)._cut_order ? `cut:${props(item)._cut_order}` : '';
  const service = item => props(item)._restposten_plan_role === 'service' || props(item)._cut_kind === 'service';
  const positive = value => Number.isFinite(Number(value)) && Number(value) > 0;
  const normalId = id => Object.keys(pairs).find(key => Number(pairs[key]) === Number(id) || Number(dpdPairs[key]) === Number(id)) || String(id);
  const parcelId = id => pairs[normalId(id)];
  const isCandidate = item => !!parcelId(item.id) && (Number(item.id) !== Number(normalId(item.id)) || ['parcel', 'parcel-eligible-blocked'].includes(props(item)._shipping_after_cut));
  function valid(p) { return ['length', 'width', 'height', 'weightGrams'].every(key => positive(p[key])); }
  function eligible(p) { return valid(p) && Object.keys(limits).every(key => p[key] <= limits[key]); }
  function profileTier(p) {
    if (!valid(p)) return '';
    // Enclosing stacked package, not merely the material's solid volume.
    return Object.keys(profileLimits).find(tier => {
      const limit = profileLimits[tier];
      return Object.keys(limits).every(key => p[key] <= limit[key]) && p.length * p.width * p.height <= limit.volume;
    }) || '';
  }
  function summary(boxes, weightGrams) {
    if (!boxes.length || boxes.some(b => b.some(n => !positive(n)))) return { eligible: false };
    const normalized = boxes.map(b => [...b].sort((a, c) => c - a));
    const p = { length: Math.ceil(Math.max(...normalized.map(b => b[0]))), width: Math.ceil(Math.max(...normalized.map(b => b[1]))), height: Math.ceil(normalized.reduce((s, b) => s + b[2], 0)), weightGrams: Math.ceil(weightGrams) };
    return { ...p, eligible: eligible(p) };
  }
  function sectionSize(product, variant) {
    const index = (product?.optionNames || []).findIndex(name => /abmess|dimension|durchmesser|diameter|variante/i.test(name));
    if (index < 0) return null;
    const size = String(variant.options?.[index] || '').replace(/,/g, '.').match(/\d+(?:\.\d+)?/g)?.map(Number);
    if (!size?.length) return null;
    // An enclosing square is conservative for complex profile sections (Z, C, handrail).
    const side = /\bZ[- ]?Profil|Ankerprofil/i.test(String(product?.title || '')) ? size.reduce((a,b)=>a+b,0) : Math.max(...size);
    return positive(side) ? [side, side] : null;
  }
  function profilePlan(result) {
    const boxes = []; let weight = 0;
    for (const bar of result.bars || []) {
      const size = sectionSize(result.profile, bar.variant);
      if (!size || !positive(bar.variant.grams) || !Array.isArray(bar.pieces)) return { eligible: false };
      const quantity = Math.max(1, Number(bar.quantity || 1));
      weight += Number(bar.variant.grams) * quantity; // Includes the delivered offcuts; no optimistic weight reduction.
      for (let i = 0; i < quantity; i++) {
        bar.pieces.forEach(length => boxes.push([Number(length), ...size]));
        (bar.restPieces || [bar.rest]).filter(positive).forEach(length => boxes.push([Number(length), ...size]));
      }
    }
    const p = summary(boxes, weight), tier = profileTier(p);
    return {...p, eligible:!!tier, tier};
  }
  function gratingPlan(result, kerf = 0) {
    const title = String(result.product?.title || '');
    const pHeight = title.match(/\bP\s*(\d+)\b/i), isoHeight = title.match(/\bISO\s*(\d+)\b/i);
    const thickness = pHeight ? Number(pHeight[1]) + 3 : isoHeight ? Number(isoHeight[1]) : 0;
    const boxes = []; let weight = 0;
    for (const plate of result.plates || []) {
      const variant = plate.variant || result.variant;
      const L = Number(variant?.length), W = Number(variant?.width), grams = Number(variant?.grams);
      if (!positive(L) || !positive(W) || !positive(grams) || !thickness || !plate.rows?.length) return { eligible: false };
      let area = 0;
      for (const row of plate.rows) {
        for (const piece of row.pieces || []) {
          boxes.push([Number(piece.length), Number(piece.width), thickness]);
          area += Number(piece.length) * Number(piece.width);
          // Shelf packing also cuts a shorter piece out of a taller row.
          if (result.offcutsIncluded && !plate.restPieces && row.height - piece.width > kerf) boxes.push([Number(piece.length), row.height - piece.width - kerf, thickness]);
        }
        if (result.offcutsIncluded && !plate.restPieces && L - row.usedLength > kerf) boxes.push([L - row.usedLength - kerf, Number(row.height), thickness]);
      }
      const last = plate.rows[plate.rows.length - 1];
      if (result.offcutsIncluded && !plate.restPieces && W - last.y - last.height > kerf) boxes.push([L, W - last.y - last.height - kerf, thickness]);
      if (result.offcutsIncluded && plate.restPieces) plate.restPieces.forEach(p => boxes.push([p.length, p.width, thickness]));
      weight += result.offcutsIncluded ? grams : grams * area / (L * W);
    }
    return summary(boxes, weight);
  }
  // Explicitly ordered transport cuts. Finished customer pieces and raw-material quantities never change.
  function splitRest(length, limit, kerf) {
    if (!Number.isFinite(length) || length < 0 || !positive(limit) || !Number.isFinite(kerf) || kerf < 0) return null;
    if (!length) return [];
    const count = Math.max(1, Math.ceil((length + kerf) / (limit + kerf)));
    if (count > 200) return null;
    const remaining = length - (count - 1) * kerf;
    if (remaining < count) return null;
    // Balanced millimetre lengths avoid creating an unnecessary tiny last remnant.
    const short = Math.floor(remaining / count);
    const pieces = Array.from({length:count}, (_, i) => short + (i < Math.floor(remaining - short * count) ? 1 : 0));
    pieces[count - 1] += remaining - pieces.reduce((a,b) => a+b,0);
    return pieces.every(p => p > 0 && p <= limit) ? pieces : null;
  }
  function plateRests(plate, variant, kerf) {
    const L = Number(variant.length), W = Number(variant.width), rests = [];
    const add = (x,y,length,width) => { if (length > 0 && width > 0) rests.push({x,y,length,width}); };
    for (const row of plate.rows || []) {
      for (const p of row.pieces || []) add(Number(p.x), Number(p.y) + Number(p.width) + kerf, Number(p.length), Number(row.height) - Number(p.width) - kerf);
      add(Number(row.usedLength) + kerf, Number(row.y), L - Number(row.usedLength) - kerf, Number(row.height));
    }
    const last = plate.rows?.[plate.rows.length - 1];
    if (last) add(0, Number(last.y) + Number(last.height) + kerf, L, W - Number(last.y) - Number(last.height) - kerf);
    return rests;
  }
  function splitPlateRest(rest, kerf) {
    const candidates = [[1000,760],[760,1000]].map(([L,W]) => {
      const xs = splitRest(rest.length,L,kerf), ys = splitRest(rest.width,W,kerf);
      if (!xs || !ys || xs.length * ys.length > 200) return null;
      const pieces = [], lines = []; let x = rest.x;
      xs.forEach((length,i) => {
        let y = rest.y;
        ys.forEach((width,j) => {
          pieces.push({x,y,length,width});
          if (j < ys.length-1) lines.push({x1:x,y1:y+width+kerf/2,x2:x+length,y2:y+width+kerf/2});
          y += width+kerf;
        });
        if (i < xs.length-1) lines.push({x1:x+length+kerf/2,y1:rest.y,x2:x+length+kerf/2,y2:rest.y+rest.width});
        x += length+kerf;
      });
      return {pieces,lines};
    }).filter(Boolean).sort((a,b) => a.lines.length-b.lines.length);
    return candidates[0] || null;
  }
  function parcelRestAlternative(result, kerf, additionalPrice) {
    if (!result || !Number.isFinite(kerf) || kerf < 0 || !Number.isFinite(additionalPrice) || additionalPrice < 0) return null;
    const profile = !!result.bars;
    const before = profile ? profilePlan(result) : gratingPlan(result,kerf);
    if (before.eligible || (!profile && !result.offcutsIncluded)) return null;
    const finish = (plan,extraCuts) => {
      if (!extraCuts || extraCuts > 200) return null;
      const after = profile ? profilePlan(plan) : gratingPlan(plan,kerf);
      if (!after.eligible) return null; // Includes all remnants, full raw weight, stack dimensions and volume.
      const extraPrice = extraCuts * additionalPrice;
      const countKey = profile ? 'cutCount' : 'totalCuts';
      return {...plan, [countKey]:result[countKey]+extraCuts, servicePrice:result.servicePrice+extraPrice,
        totalPrice:result.totalPrice+extraPrice, offcutParcel:{extraCuts,extraPrice,maxLength:after.length,tier:after.tier || 'parcel'}};
    };
    if (profile) {
      const candidates = [2000,1200].map(limit => {
        let extraCuts = 0;
        const bars = result.bars.map(bar => {
          const restPieces = splitRest(Number(bar.rest),limit,kerf);
          if (!restPieces) return null;
          const restCuts = Math.max(0,restPieces.length-1);
          extraCuts += restCuts * Math.max(1,Number(bar.quantity || 1));
          return {...bar,restPieces,restCuts,rest:restPieces.reduce((a,b)=>a+b,0),kerfLoss:bar.kerfLoss+restCuts*kerf,used:bar.used+restCuts*kerf};
        });
        if (bars.some(b=>!b)) return null;
        return finish({...result,bars,kerfLoss:result.kerfLoss+extraCuts*kerf,
          rest:bars.reduce((s,b)=>s+b.rest*Math.max(1,Number(b.quantity||1)),0)},extraCuts);
      }).filter(Boolean);
      // Fewest additional paid cuts; prefer the lower parcel tier when the cutting cost is equal.
      return candidates.sort((a,b)=>a.offcutParcel.extraCuts-b.offcutParcel.extraCuts || Number(b.offcutParcel.tier==='dpd')-Number(a.offcutParcel.tier==='dpd'))[0] || null;
    }
    let extraCuts = 0;
    const plates = (result.plates || []).map(plate => {
      const variant = plate.variant || result.variant;
      const layouts = plateRests(plate,variant,kerf).map(r=>splitPlateRest(r,kerf));
      if (layouts.some(l=>!l)) return null;
      const restCutLines = layouts.flatMap(l=>l.lines);
      extraCuts += restCutLines.length;
      return {...plate,restPieces:layouts.flatMap(l=>l.pieces),restCutLines};
    });
    if (plates.some(p=>!p)) return null;
    return finish({...result,plates},extraCuts);
  }
  const readSummary = item => ({ length: Number(props(item)._shipping_finished_length_mm), width: Number(props(item)._shipping_finished_width_mm), height: Number(props(item)._shipping_finished_height_mm), weightGrams: Number(props(item)._shipping_finished_weight_g) });
  const materialKey = items => {
    const counts = new Map();
    items.filter(item => !service(item)).forEach(item => counts.set(String(item.id), (counts.get(String(item.id)) || 0) + Number(item.quantity)));
    return Array.from(counts).sort((a,b) => a[0].localeCompare(b[0])).map(([id,q]) => `${id}:${q}`).join(',');
  };
  function plainSummary(item) {
    const options = item.options_with_values || [];
    const lengthOption = options.find(o => /^(länge|laenge|length|longueur)$/i.test(o.name || ''));
    const sizeOption = options.find(o => /abmess|dimension|durchmesser|diameter/i.test(o.name || ''));
    let sides = String(sizeOption?.value || '').replace(/,/g, '.').match(/\d+(?:\.\d+)?/g)?.map(Number) || [];
    const source = String(item.product_title || item.title || '');
    if (!sides.length) {
      const matches = Array.from(`${source} ${item.variant_title || ''}`.matchAll(/(\d{2,5})\s*[x×]\s*(\d{2,5})/gi));
      // A trailing mesh size is not the outline of the delivered restposten plate.
      if (matches.length) {
        const match = matches.reduce((largest, candidate) => Number(candidate[1]) * Number(candidate[2]) > Number(largest[1]) * Number(largest[2]) ? candidate : largest);
        sides = [Number(match[1]), Number(match[2])];
      }
    }
    const quantity = Number(item.quantity || 1), grams = Number(item.grams || 0);
    const rawLength = String(lengthOption?.value || '').replace(',', '.').match(/\d+(?:\.\d+)?/);
    if (rawLength && sides.length) {
      const cross = Math.max(...sides);
      return summary(Array.from({length:quantity}, () => [Number(rawLength[0]), cross, cross]), grams * quantity);
    }
    const closed = source.match(/\bP\s*(\d+)\b/i), iso = source.match(/\bISO\s*(\d+)\b/i), explicit = source.match(/\b(\d+)\s*mm\s*(?:hoch|höhe)\b/i);
    const thickness = closed ? Number(closed[1]) + 3 : iso ? Number(iso[1]) : explicit ? Number(explicit[1]) : 0;
    if (sides.length >= 2 && thickness) return summary(Array.from({length:quantity}, () => [sides[0],sides[1],thickness]), grams * quantity);
    return { eligible: false }; // Unknown shippable articles never silently enable the override.
  }
  function cartSummary(cart) {
    let total = { length:0, width:0, height:0, weightGrams:0 }, seen = new Set();
    const items = cart?.items || [];
    for (const item of items) {
      if (service(item) || item.requires_shipping === false) continue;
      const id = groupId(item);
      let p;
      if (id) {
        if (seen.has(id)) continue;
        seen.add(id);
        const group = items.filter(other => groupId(other) === id);
        const marker = group.find(isCandidate);
        if (marker) {
          if (Number(marker.quantity) !== 1) return { eligible:false };
          if (props(marker)._shipping_plan_materials && props(marker)._shipping_plan_materials !== materialKey(group)) return { eligible:false };
          p = readSummary(marker);
        } else {
          // Uncut standard-size orders have no paid cutting marker. Evaluate their original
          // physical product dimensions/weights, never treat missing cut data as a small parcel.
          const materials = group.filter(other => !service(other));
          if (!materials.length || group.some(service) || !materials.every(other => props(other)._cut_total_cuts === '0')) return {eligible:false};
          const parts = materials.map(plainSummary);
          if (parts.some(part => !valid(part))) return {eligible:false};
          p = {length:Math.max(...parts.map(part=>part.length)),width:Math.max(...parts.map(part=>part.width)),
            height:parts.reduce((s,part)=>s+part.height,0),weightGrams:parts.reduce((s,part)=>s+part.weightGrams,0)};
        }
      } else p = plainSummary(item);
      if (!valid(p)) return { eligible:false };
      total.length = Math.max(total.length, p.length); total.width = Math.max(total.width, p.width);
      total.height += p.height; total.weightGrams += p.weightGrams;
    }
    const hasProfileCut = items.some(item => isCandidate(item) && normalId(item.id) === '53991162085722' && groupId(item) && items.some(other => groupId(other) === groupId(item) && !service(other)));
    const tier = hasProfileCut ? profileTier(total) : '';
    return { ...total, tier, eligible:!!tier || eligible(total) };
  }
  function label(p) { return `Paketmaß nach Zuschnitt: ${p.length}×${p.width}×${p.height} mm, ca. ${(p.weightGrams / 1000).toLocaleString('de-DE', {maximumFractionDigits:1})} kg`; }
  async function getVariant(id) {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}variants/${id}.js`, {headers:{Accept:'application/json'},cache:'no-store'});
    if (!response.ok) throw new Error('shipping_variant_check_failed');
    return response.json();
  }
  function unloadingGratingPlan(result, kerf) {
    const pieces = []; let weight = 0, weightKnown = true;
    for (const plate of result.plates || []) {
      const variant = plate.variant || result.variant;
      const area = Number(variant?.length) * Number(variant?.width), grams = Number(variant?.grams);
      if (!(area > 0 && grams > 0)) weightKnown = false;
      let usedArea = 0;
      for (const row of plate.rows || []) for (const piece of row.pieces || []) {
        pieces.push([Number(piece.length),Number(piece.width)]);
        usedArea += Number(piece.length) * Number(piece.width);
      }
      if (result.offcutsIncluded) {
        (plate.restPieces || plateRests(plate,variant,kerf)).forEach(piece => pieces.push([Number(piece.length),Number(piece.width)]));
      }
      if (area > 0 && grams > 0) weight += result.offcutsIncluded ? grams : grams * usedArea / area;
    }
    if (!pieces.length || pieces.some(piece => piece.some(n => !(n > 0)))) return {};
    return {length:Math.max(...pieces.map(p => Math.max(...p))),width:Math.max(...pieces.map(p => Math.min(...p))),weightGrams:weightKnown ? Math.ceil(weight) : 0};
  }
  async function prepare(owner, result, items) {
    // Keep delivered dimensions for every order, including freight and uncut sizes.
    // Parcel eligibility must not decide whether unloading can be checked later.
    const delivery = result.bars ? profilePlan(result) : unloadingGratingPlan(result, Number(owner.config.kerf || 0));
    const materials = materialKey(items);
    items.filter(item => !service(item)).forEach(item => {
      item.properties = {...item.properties,
        _unloading_kind: result.bars ? 'profile' : 'grating',
        _unloading_delivery: JSON.stringify([delivery.length || 0, delivery.width || 0, delivery.weightGrams || 0]),
        _unloading_materials: materials};
    });
    const cutCount = Number(result.cutCount ?? result.totalCuts ?? 0);
    if (!cutCount) return;
    const p = result.bars ? profilePlan(result) : gratingPlan(result, Number(owner.config.kerf || 0));
    if (!p.eligible) return;
    const first = items.find(item => service(item) && parcelId(item.id));
    if (!first) return;
    // Check current service prices before selecting an equivalent variant. Never alter cutting charges.
    const targetId = p.tier === 'dpd' ? dpdPairs[normalId(first.id)] : parcelId(first.id);
    const [standard, parcel] = await Promise.all([getVariant(normalId(first.id)), getVariant(targetId)]);
    if (!parcel.available || Number(parcel.price) !== Number(standard.price)) return;
    first.properties = {...first.properties, _shipping_after_cut:'parcel-eligible-blocked', _shipping_finished_length_mm:String(p.length), _shipping_finished_width_mm:String(p.width), _shipping_finished_height_mm:String(p.height), _shipping_finished_weight_g:String(p.weightGrams), _shipping_plan_materials:materialKey(items), _shipping_includes_offcuts:result.bars || result.offcutsIncluded ? 'true':'false'};
    // Begin with the ordinary service; the cart enables the parcel SKU only after checking every order/article.
    first.id = Number(normalId(first.id));
    first.properties.Versandprüfung = 'Paketmaß nach Zuschnitt geprüft; gesamter Warenkorb wird vor dem Checkout geprüft';
  }
  function initCart() {
    if (window.__fibroluxCutParcelGuard) return;
    window.__fibroluxCutParcelGuard = true;
    let running = false, queued = false, blocked = true;
    const root = window.Shopify?.routes?.root || '/';
    function lock(value, message='') {
      blocked = value;
      document.querySelectorAll('button[name="checkout"], [href="/checkout"]').forEach(el => { el.toggleAttribute('disabled',value); el.setAttribute('aria-disabled',String(value)); });
      document.querySelectorAll('.additional-checkout-buttons').forEach(el => {el.style.pointerEvents=value?'none':'';el.style.opacity=value?'0.5':'';});
      document.querySelectorAll('#cart-errors, #CartDrawer-CartErrors').forEach(el => {
        let warning = el.querySelector('[data-parcel-warning]');
        if (!message) { warning?.remove(); return; }
        if (!warning) {warning=document.createElement('p');warning.dataset.parcelWarning='';warning.setAttribute('role','alert');el.appendChild(warning);}
        warning.textContent=message;
      });
    }
    const intercept = event => {if (blocked && (event.type==='submit' ? event.submitter?.name==='checkout' : event.target.closest('button[name="checkout"], a[href*="/checkout"], .additional-checkout-buttons'))) {event.preventDefault();event.stopImmediatePropagation();}};
    document.addEventListener('click',intercept,true); document.addEventListener('submit',intercept,true);
    async function enforce() {
      if (running) {queued=true;return;}
      running=true;lock(true);
      try {
        const response = await fetch(`${root}cart.js`,{headers:{Accept:'application/json'},cache:'no-store'});
        if (!response.ok) throw new Error('cart_fetch_failed');
        const cart=await response.json(), candidates=(cart.items||[]).filter(isCandidate);
        if (!candidates.length) {lock(false);return;}
        const cartPackage=cartSummary(cart), canParcel=cartPackage.eligible;
        const changes=[];
        for (const item of candidates) {
          const itemGroup = groupId(item);
          const groupConfirmed = !!itemGroup && cart.items.some(other => groupId(other)===itemGroup && !service(other));
          const enable = canParcel && groupConfirmed;
          let desired=enable ? (cartPackage.tier==='dpd' && dpdPairs[normalId(item.id)] || parcelId(item.id)) : Number(normalId(item.id));
          if (Number(item.id)===desired) continue;
          if (enable) {
            const replacement=await getVariant(desired);
            if (!replacement.available || Number(replacement.price)!==Number(item.original_price ?? item.price)) continue;
          }
          const properties={...props(item),_shipping_after_cut:enable?'parcel':'parcel-eligible-blocked',Versandprüfung:enable?label(readSummary(item)):'Regulärer Versand: Paketgrenzen im gesamten Warenkorb nicht bestätigt'};
          changes.push({item,replacement:{id:desired,quantity:item.quantity,properties}});
        }
        if (!changes.length) {lock(false);return;}
        const add=await fetch(`${root}cart/add.js`,{method:'POST',headers:{'Content-Type':'application/json',Accept:'application/json'},body:JSON.stringify({items:changes.map(c=>c.replacement)})});
        if (!add.ok) throw new Error('cart_add_failed');
        const update=await fetch(`${root}cart/update.js`,{method:'POST',headers:{'Content-Type':'application/json',Accept:'application/json'},body:JSON.stringify({updates:Object.fromEntries(changes.map(c=>[c.item.key,0]))})});
        if (!update.ok) throw new Error('cart_update_failed');
        window.location.reload();
      } catch (error) {
        console.error('[FibroluxCutParcelGuard]',error);
        lock(true,'Die Versandprüfung konnte nicht abgeschlossen werden. Bitte laden Sie den Warenkorb neu.');
      } finally {running=false;if(queued){queued=false;schedule();}}
    }
    function schedule() {lock(true);window.requestAnimationFrame(enforce);}
    if (typeof subscribe==='function') subscribe(PUB_SUB_EVENTS.cartUpdate,schedule);
    if(document.querySelector('cart-items, cart-drawer-items')) schedule(); else blocked=false;
  }
  window.FibroluxCutShipping={limits,profileLimits,pairs,dpdPairs,normalId,parcelId,isCandidate,summary,profileTier,sectionSize,profilePlan,gratingPlan,splitRest,plateRests,parcelRestAlternative,plainSummary,cartSummary,materialKey,prepare,initCart};
})();
